import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getStoredConsent, saveConsent } from "../lib/consent";

// Banner consenso cookie sobrio, nel design system del sito.
// Nessun dark pattern: Accetta e Rifiuta hanno lo stesso rilievo.
// "Gestisci preferenze" espande il pannello con il dettaglio marketing.
// Riapribile in qualsiasi momento dall'evento globale "loi:open-cookie-preferences".
export const CookieConsent = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [panel, setPanel] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored) {
      setMarketing(!!stored.marketing);
    } else {
      const id = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(id);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const open = () => {
      setMarketing(!!getStoredConsent()?.marketing);
      setPanel(true);
      setVisible(true);
    };
    window.addEventListener("loi:open-cookie-preferences", open);
    return () => window.removeEventListener("loi:open-cookie-preferences", open);
  }, []);

  if (!visible) return null;

  const choose = (value) => {
    saveConsent(value);
    setVisible(false);
    setPanel(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[70] px-4 pb-4 sm:px-6 sm:pb-6" data-testid="cookie-banner" role="dialog" aria-label={t("consent.title")}>
      <div className="mx-auto max-w-3xl border border-white/10 bg-[#16191D] p-6 shadow-2xl shadow-black/40 sm:p-7">
        <p className="font-display text-lg font-light text-[#F9F8F6]">{t("consent.title")}</p>
        <p className="mt-2 text-xs leading-relaxed text-[#A0A0A0]">{t("consent.text")}</p>

        {panel && (
          <div className="mt-5 space-y-3 border-t border-white/10 pt-5" data-testid="cookie-panel">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-[#F9F8F6]">{t("consent.necessary")}</p>
                <p className="mt-0.5 text-[11px] leading-snug text-[#666]">{t("consent.necessaryDesc")}</p>
              </div>
              <span className="shrink-0 font-code text-[9px] uppercase tracking-[0.2em] text-[#666]">{t("consent.alwaysOn")}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-[#F9F8F6]">{t("consent.marketing")}</p>
                <p className="mt-0.5 text-[11px] leading-snug text-[#666]">{t("consent.marketingDesc")}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={marketing}
                onClick={() => setMarketing(!marketing)}
                className={`flex h-7 w-12 shrink-0 items-center rounded-full border px-1 transition-[background-color,border-color] duration-300 ${
                  marketing ? "justify-end border-[#D6705A] bg-[#D6705A]" : "justify-start border-white/20 bg-white/5"
                }`}
                data-testid="cookie-marketing-toggle"
              >
                <span className={`h-5 w-5 rounded-full ${marketing ? "bg-[#0D0F12]" : "bg-[#A0A0A0]"}`} />
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-end">
          {panel ? (
            <button
              type="button"
              onClick={() => choose(marketing)}
              className="min-h-[44px] rounded-full bg-[#D6705A] px-7 py-3 font-code text-[10px] uppercase tracking-[0.2em] text-[#0D0F12] transition-[background-color] duration-300 hover:bg-[#F9F8F6]"
              data-testid="cookie-save"
            >
              {t("consent.save")}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setPanel(true)}
                className="min-h-[44px] rounded-full px-5 py-3 font-code text-[10px] uppercase tracking-[0.2em] text-[#A0A0A0] transition-colors duration-300 hover:text-[#F9F8F6]"
                data-testid="cookie-customize"
              >
                {t("consent.customize")}
              </button>
              <button
                type="button"
                onClick={() => choose(false)}
                className="min-h-[44px] rounded-full border border-white/20 px-7 py-3 font-code text-[10px] uppercase tracking-[0.2em] text-[#F9F8F6] transition-[border-color,color] duration-300 hover:border-[#D6705A] hover:text-[#D6705A]"
                data-testid="cookie-reject"
              >
                {t("consent.reject")}
              </button>
              <button
                type="button"
                onClick={() => choose(true)}
                className="min-h-[44px] rounded-full bg-[#D6705A] px-7 py-3 font-code text-[10px] uppercase tracking-[0.2em] text-[#0D0F12] transition-[background-color] duration-300 hover:bg-[#F9F8F6]"
                data-testid="cookie-accept"
              >
                {t("consent.accept")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
